import { useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import validator from 'validator';

import { listOfLinksMicrositeAPIService } from '~/api/microsites/links';
import { CustomDropZone } from '~/components/atoms/CustomDropZone';
import GridSelector from '~/components/atoms/GridSelector';
import { UploadedImagePreview } from '~/components/atoms/UploadedImagePreview';
import SocialBlock from '~/components/organisms/editor/SocialBlock/SocialBlock';
import Accordion from '~/components/ui/Accordion/Accordion';
import { Button } from '~/components/ui/button';
import useBuilderBlockMaxWidth from '~/hooks/useBuilderBlockMaxWidth';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { listOfLinksSlice } from '~/store/reducers/previewScreens/list-of-links';
import {
  updateListOfLinksSiteThunk,
  updateListOfLinksSocialThunk,
} from '~/store/reducers/previewScreens/list-of-links';
import { getListOfLinksReducer } from '~/store/reducers/previewScreens/list-of-links/selectors';

import { ImageItem } from '../sub-components/ItemImage';

const { actions } = listOfLinksSlice;

export default function Content() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    micrositeData,
    content: { socialLinks, linkList, images, imagesType },
    isDeleteSocialLoading,
  } = useSelector(getListOfLinksReducer);

  const gridSelectorMaxWidth = useBuilderBlockMaxWidth();

  const [stopTrigger, setStopTrigger] = useState(false);
  const [dragTrigger, setDragTrigger] = useState(0);

  const handleLink = async () => {
    setStopTrigger(true);

    const response = await listOfLinksMicrositeAPIService.createLink({
      text: '',
      url: '',
      photo_id: null,
      links_microsite: micrositeData?.id,
      position: linkList.length + 1,
    });

    dispatch(
      actions.setContentLinkList([
        ...linkList,
        { previewLink: '', url: '', linkText: '', id: `UNIQUE_ID_${linkList.length}`, unique_id: response?.data?.id },
      ]),
    );

    setStopTrigger(false);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(linkList, result.source.index, result.destination.index);

    dispatch(actions.setContentLinkList(items));
    setDragTrigger((trigger) => trigger + 1);
    setStopTrigger(false);
  };

  const addImages = (files) => {
    let newImages = files.map((itemFile) => {
      return {
        img: itemFile.previewLink,
        title: itemFile.name || itemFile.path,
        imageStorageId: itemFile.imageStorageId,
        size: itemFile.size,
        microsite: micrositeData?.id,
        isLoading: true,
      };
    });

    dispatch(actions.setImages([...images, ...newImages]));

    newImages.forEach(async (item, index) => {
      const res = await listOfLinksMicrositeAPIService.addImage({
        title: item.title,
        image_id: item.imageStorageId,
        size: item.size,
        microsite: item?.microsite,
        position: index,
      });

      if (res?.data?.id) {
        newImages = newImages.map((item, currentIndex) =>
          currentIndex === index ? { ...item, id: res?.data?.id, isLoading: false } : item,
        );

        dispatch(actions.setImages([...images, ...newImages]));
      }
    });
  };

  const removeImage = async (id) => {
    listOfLinksMicrositeAPIService.removeImage(id);

    dispatch(actions.setImages(images.filter((item) => item?.id !== id)));
  };

  const handleSocialLinks = (val, type, index) => {
    if (micrositeData) {
      const addingValue = {
        network: val[index]?.network?.toUpperCase() || '',
        url: val[index]?.value,
        title: val[index]?.title,
        microsite: micrositeData?.id,
      };

      const areValValid = val.every((item) => validator.isURL(item.value));

      dispatch(actions.setSocialLinks(val));

      if (areValValid) {
        switch (type) {
          case 'add':
            return dispatch(
              updateListOfLinksSocialThunk({
                id: micrositeData?.id,
                body: addingValue,
                type: 'add',
              }),
            );
          case 'update':
            return dispatch(
              updateListOfLinksSocialThunk({
                id: micrositeData?.id,
                body: {
                  id: micrositeData?.socials[index]?.id,
                  data: addingValue,
                },
                type: 'update',
              }),
            );
          case 'delete':
            return dispatch(
              updateListOfLinksSocialThunk({
                id: micrositeData?.id,
                body: micrositeData?.socials[index]?.id,
                type: 'delete',
              }),
            );
          default:
            break;
        }
      } else {
        dispatch(actions.setSocialLinks(val));
      }
    }
  };

  const setGridType = (type) => {
    dispatch(
      updateListOfLinksSiteThunk({
        id: micrositeData.id,
        body: { images_view_type: type },
        dispatchHandler: () => dispatch(actions.setImagesType(type)),
      }),
    );
  };

  return (
    <CreateQRContentBlockLayout type="content">
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('template2:links')}
        details={
          <>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {linkList.map((item, index, array) => {
                      return (
                        <ImageItem
                          key={item?.id}
                          item={item}
                          index={index}
                          linkList={array}
                          stopTrigger={stopTrigger}
                          micrositeId={micrositeData?.id}
                          dragTrigger={dragTrigger}
                        />
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>

            <Button type="primary-outlined" endIcon={<AddIcon />} onClick={handleLink} size="xs">
              {t('buttons:addLink')}
            </Button>
          </>
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('template2:titleImages')}
        details={
          <>
            <CustomDropZone
              fileTypes="PNG, JPEG"
              maxSizeInKb={15000}
              maxSize={15}
              onDrop={(files) => addImages(files)}
              existedLength={images.length}
              acceptTypes="image/*"
            />
            {images.map((item) => {
              return (
                <UploadedImagePreview
                  key={item?.id}
                  imageName={item?.title}
                  imageSize={item?.size}
                  id={item?.id}
                  deleteHandler={removeImage}
                  isLoading={item?.isLoading}
                  imgSrc={item.img || item.image}
                />
              );
            })}
            <GridSelector
              maxWidth={gridSelectorMaxWidth}
              setImagesType={(type) => setGridType(type)}
              defaultImageType={imagesType}
            />
          </>
        }
      />
      <SocialBlock
        dispatchHandler={(val) => dispatch(actions.setSocialLinks(val))}
        setSocialLinks={handleSocialLinks}
        socialLinks={socialLinks}
        renderLabel={false}
        isDeleteDisabled={isDeleteSocialLoading}
      />
    </CreateQRContentBlockLayout>
  );
}
